/**
 * Spline2D Component
 *
 * 2D spline path for camera rails, movement paths, and other uses.
 * Control points are referenced GameObjects - just drag empty objects to define the path.
 * Supports Linear, Catmull-Rom, and Bezier interpolation.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  field,
  customEditor,
} from "../attributes";
import type { Level } from "../Level";

/** Spline interpolation type */
export enum SplineType {
  /** Straight lines between points */
  Linear = 0,
  /** Smooth Catmull-Rom spline (passes through all points) */
  CatmullRom = 1,
  /** Cubic Bezier with auto-smooth handles */
  Bezier = 2,
}

/** A resolved point with position and handles */
export interface ResolvedSplinePoint {
  /** X world position */
  x: number;
  /** Y world position */
  y: number;
  /** Auto-calculated or explicit handle for incoming tangent */
  tanInX: number;
  /** Auto-calculated or explicit handle for incoming tangent */
  tanInY: number;
  /** Auto-calculated or explicit handle for outgoing tangent */
  tanOutX: number;
  /** Auto-calculated or explicit handle for outgoing tangent */
  tanOutY: number;
}

@component("core.Spline2D")
@customEditor("Spline2DEditor")
@displayName("Spline 2D")
@icon("Spline")
@color("#10b981")
@category("Path")
@allowMultiple(false)
export class Spline2D {
  /** Spline interpolation type */
  @field({ type: "i32", label: "Type", tooltip: "0=Linear, 1=CatmullRom, 2=Bezier" })
  splineType: number = SplineType.CatmullRom;

  /** Whether the spline forms a closed loop */
  @field({ type: "bool", label: "Closed Loop" })
  closed: boolean = false;

  /** Catmull-Rom tension (0 = straight, 0.5 = default, 1 = tight) */
  @field({ type: "f32", label: "Tension", min: 0, max: 1, step: 0.01 })
  tension: number = 0.5;

  /** Bezier smoothness for auto-handles (0 = sharp, 1 = very smooth) */
  @field({ type: "f32", label: "Smoothness", min: 0, max: 1, step: 0.01 })
  smoothness: number = 0.3;

  /** 
   * Control point GameObject GUIDs.
   * Each GUID references a GameObject whose position defines a point on the spline.
   */
  @field({ type: "array", label: "Control Points" })
  controlPoints: string[] = [];

  /**
   * Per-point smoothness for Bezier (0 = sharp, 1 = very smooth).
   * Array length should match controlPoints. Missing values default to global smoothness.
   */
  @field({ type: "array", label: "Point Strengths", hidden: true })
  controlPointStrengths: number[] = [];

  /** Color for gizmo rendering */
  @field({ type: "color", label: "Gizmo Color" })
  gizmoColor: string = "#10b981";

  /** Show direction arrows on spline */
  @field({ type: "bool", label: "Show Direction" })
  showDirection: boolean = true;

  /** Show connecting lines to control point objects */
  @field({ type: "bool", label: "Show Point Links" })
  showPointLinks: boolean = true;
}

// ============================================================================
// SPLINE MATH UTILITIES
// ============================================================================

/**
 * Evaluate a point on a linear segment
 */
export function evalLinear(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  t: number
): { x: number; y: number } {
  return {
    x: p0.x + (p1.x - p0.x) * t,
    y: p0.y + (p1.y - p0.y) * t,
  };
}

/**
 * Evaluate a point on a Catmull-Rom spline segment
 */
export function evalCatmullRom(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number,
  tension: number = 0.5
): { x: number; y: number } {
  const t2 = t * t;
  const t3 = t2 * t;
  const s = (1 - tension) / 2;

  const b0 = -s * t3 + 2 * s * t2 - s * t;
  const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
  const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
  const b3 = s * t3 - s * t2;

  return {
    x: p0.x * b0 + p1.x * b1 + p2.x * b2 + p3.x * b3,
    y: p0.y * b0 + p1.y * b1 + p2.y * b2 + p3.y * b3,
  };
}

/**
 * Evaluate a point on a cubic Bezier curve
 */
export function evalBezier(
  p0: { x: number; y: number },
  cp1: { x: number; y: number },
  cp2: { x: number; y: number },
  p1: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const t2 = t * t;
  const t3 = t2 * t;
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;

  return {
    x: mt3 * p0.x + 3 * mt2 * t * cp1.x + 3 * mt * t2 * cp2.x + t3 * p1.x,
    y: mt3 * p0.y + 3 * mt2 * t * cp1.y + 3 * mt * t2 * cp2.y + t3 * p1.y,
  };
}

/**
 * Evaluate tangent of a Catmull-Rom spline
 */
export function evalCatmullRomTangent(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number,
  tension: number = 0.5
): { x: number; y: number } {
  const t2 = t * t;
  const s = (1 - tension) / 2;

  const db0 = -3 * s * t2 + 4 * s * t - s;
  const db1 = 3 * (2 - s) * t2 + 2 * (s - 3) * t;
  const db2 = 3 * (s - 2) * t2 + 2 * (3 - 2 * s) * t + s;
  const db3 = 3 * s * t2 - 2 * s * t;

  return {
    x: p0.x * db0 + p1.x * db1 + p2.x * db2 + p3.x * db3,
    y: p0.y * db0 + p1.y * db1 + p2.y * db2 + p3.y * db3,
  };
}

/**
 * Evaluate tangent of a cubic Bezier curve
 */
export function evalBezierTangent(
  p0: { x: number; y: number },
  cp1: { x: number; y: number },
  cp2: { x: number; y: number },
  p1: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const t2 = t * t;
  const mt = 1 - t;
  const mt2 = mt * mt;

  return {
    x: 3 * mt2 * (cp1.x - p0.x) + 6 * mt * t * (cp2.x - cp1.x) + 3 * t2 * (p1.x - cp2.x),
    y: 3 * mt2 * (cp1.y - p0.y) + 6 * mt * t * (cp2.y - cp1.y) + 3 * t2 * (p1.y - cp2.y),
  };
}

/**
 * Calculate auto-smooth Bezier handles for a point based on neighbors
 */
export function calculateAutoHandles(
  prev: { x: number; y: number } | null,
  current: { x: number; y: number },
  next: { x: number; y: number } | null,
  smoothness: number = 0.3
): { tanInX: number; tanInY: number; tanOutX: number; tanOutY: number } {
  // If no neighbors, handles are at point
  if (!prev && !next) {
    return { tanInX: 0, tanInY: 0, tanOutX: 0, tanOutY: 0 };
  }

  // Calculate tangent direction from neighbors
  let tx = 0, ty = 0;
  
  if (prev && next) {
    // Smooth: tangent is parallel to line from prev to next
    tx = (next.x - prev.x) * smoothness;
    ty = (next.y - prev.y) * smoothness;
  } else if (prev) {
    // End point: tangent toward prev
    tx = (current.x - prev.x) * smoothness;
    ty = (current.y - prev.y) * smoothness;
  } else if (next) {
    // Start point: tangent toward next
    tx = (next.x - current.x) * smoothness;
    ty = (next.y - current.y) * smoothness;
  }

  return {
    tanInX: -tx,
    tanInY: -ty,
    tanOutX: tx,
    tanOutY: ty,
  };
}

// ============================================================================
// SPLINE EVALUATOR CLASS
// ============================================================================

/**
 * Spline evaluator that works with resolved positions
 */
export class SplineEvaluator {
  private points: ResolvedSplinePoint[];
  private splineType: SplineType;
  private closed: boolean;
  private tension: number;

  constructor(
    points: ResolvedSplinePoint[],
    splineType: SplineType = SplineType.CatmullRom,
    closed: boolean = false,
    tension: number = 0.5
  ) {
    this.points = points;
    this.splineType = splineType;
    this.closed = closed;
    this.tension = tension;
  }

  /** Get the resolved points */
  getPoints(): ReadonlyArray<ResolvedSplinePoint> {
    return this.points;
  }

  /** Get number of segments */
  get segmentCount(): number {
    if (this.points.length < 2) return 0;
    return this.closed ? this.points.length : this.points.length - 1;
  }

  /** Get total "length" in terms of t (0 to segmentCount) */
  get totalT(): number {
    return this.segmentCount;
  }

  /**
   * Evaluate position at parameter t (0 to segmentCount)
   */
  evaluate(t: number): { x: number; y: number } {
    if (this.points.length === 0) return { x: 0, y: 0 };
    if (this.points.length === 1) return { x: this.points[0].x, y: this.points[0].y };

    const maxT = this.segmentCount;
    if (this.closed) {
      t = ((t % maxT) + maxT) % maxT;
    } else {
      t = Math.max(0, Math.min(maxT, t));
    }

    const segmentIndex = Math.min(Math.floor(t), this.segmentCount - 1);
    const localT = t - segmentIndex;

    return this.evaluateSegment(segmentIndex, localT);
  }

  /**
   * Evaluate tangent (direction) at parameter t
   */
  evaluateTangent(t: number): { x: number; y: number } {
    if (this.points.length < 2) return { x: 1, y: 0 };

    const maxT = this.segmentCount;
    if (this.closed) {
      t = ((t % maxT) + maxT) % maxT;
    } else {
      t = Math.max(0, Math.min(maxT - 0.0001, t));
    }

    const segmentIndex = Math.min(Math.floor(t), this.segmentCount - 1);
    const localT = t - segmentIndex;

    return this.evaluateSegmentTangent(segmentIndex, localT);
  }

  /**
   * Get rotation angle at parameter t (radians)
   */
  evaluateRotation(t: number): number {
    const tan = this.evaluateTangent(t);
    return Math.atan2(tan.y, tan.x);
  }

  private getPoint(i: number): ResolvedSplinePoint {
    const n = this.points.length;
    if (this.closed) {
      return this.points[((i % n) + n) % n];
    }
    return this.points[Math.max(0, Math.min(n - 1, i))];
  }

  private evaluateSegment(segmentIndex: number, localT: number): { x: number; y: number } {
    const p1 = this.getPoint(segmentIndex);
    const p2 = this.getPoint(segmentIndex + 1);

    switch (this.splineType) {
      case SplineType.Linear:
        return evalLinear(p1, p2, localT);

      case SplineType.CatmullRom: {
        const p0 = this.getPoint(segmentIndex - 1);
        const p3 = this.getPoint(segmentIndex + 2);
        return evalCatmullRom(p0, p1, p2, p3, localT, this.tension);
      }

      case SplineType.Bezier: {
        const cp1 = { x: p1.x + p1.tanOutX, y: p1.y + p1.tanOutY };
        const cp2 = { x: p2.x + p2.tanInX, y: p2.y + p2.tanInY };
        return evalBezier(p1, cp1, cp2, p2, localT);
      }

      default:
        return evalLinear(p1, p2, localT);
    }
  }

  private evaluateSegmentTangent(segmentIndex: number, localT: number): { x: number; y: number } {
    const p1 = this.getPoint(segmentIndex);
    const p2 = this.getPoint(segmentIndex + 1);

    switch (this.splineType) {
      case SplineType.Linear: {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        return { x: dx / len, y: dy / len };
      }

      case SplineType.CatmullRom: {
        const p0 = this.getPoint(segmentIndex - 1);
        const p3 = this.getPoint(segmentIndex + 2);
        const tan = evalCatmullRomTangent(p0, p1, p2, p3, localT, this.tension);
        const len = Math.sqrt(tan.x * tan.x + tan.y * tan.y) || 1;
        return { x: tan.x / len, y: tan.y / len };
      }

      case SplineType.Bezier: {
        const cp1 = { x: p1.x + p1.tanOutX, y: p1.y + p1.tanOutY };
        const cp2 = { x: p2.x + p2.tanInX, y: p2.y + p2.tanInY };
        const tan = evalBezierTangent(p1, cp1, cp2, p2, localT);
        const len = Math.sqrt(tan.x * tan.x + tan.y * tan.y) || 1;
        return { x: tan.x / len, y: tan.y / len };
      }

      default: {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        return { x: dx / len, y: dy / len };
      }
    }
  }

  /**
   * Find closest point on spline to a given position
   */
  findClosestT(px: number, py: number, resolution: number = 100): number {
    if (this.points.length < 2) return 0;

    let closestT = 0;
    let closestDist = Infinity;

    const maxT = this.segmentCount;
    for (let i = 0; i <= resolution; i++) {
      const t = (i / resolution) * maxT;
      const p = this.evaluate(t);
      const dx = p.x - px;
      const dy = p.y - py;
      const dist = dx * dx + dy * dy;
      if (dist < closestDist) {
        closestDist = dist;
        closestT = t;
      }
    }

    return closestT;
  }

  /**
   * Sample spline for rendering
   */
  sample(pointsPerSegment: number = 20): Array<{ x: number; y: number }> {
    if (this.points.length < 2) {
      return this.points.map(p => ({ x: p.x, y: p.y }));
    }

    const result: Array<{ x: number; y: number }> = [];
    const totalPoints = this.segmentCount * pointsPerSegment;

    for (let i = 0; i <= totalPoints; i++) {
      const t = (i / totalPoints) * this.segmentCount;
      result.push(this.evaluate(t));
    }

    return result;
  }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Resolve control point GUIDs to actual positions from a Level
 */
export function resolveSplinePoints(
  resolvePos: (guid: string) => { x: number; y: number } | null,
  controlPointGuids: string[],
  splineType: SplineType,
  smoothness: number = 0.3,
  closed: boolean = false,
  perPointStrengths: number[] = []
): ResolvedSplinePoint[] {
  const positions: { x: number; y: number }[] = [];

  // Resolve GUIDs to world positions (resolver injected — Level-backed or engine-API-backed)
  for (const guid of controlPointGuids) {
    const pos = resolvePos(guid);
    if (pos) {
      positions.push({ x: pos.x, y: pos.y });
    }
  }
  
  if (positions.length === 0) return [];
  
  // Calculate handles for Bezier (auto-smooth)
  const result: ResolvedSplinePoint[] = [];
  
  for (let i = 0; i < positions.length; i++) {
    const n = positions.length;
    // For closed splines, wrap around for neighbors
    const prevIdx = closed ? ((i - 1 + n) % n) : (i > 0 ? i - 1 : -1);
    const nextIdx = closed ? ((i + 1) % n) : (i < n - 1 ? i + 1 : -1);
    
    const prev = prevIdx >= 0 ? positions[prevIdx] : null;
    const curr = positions[i];
    const next = nextIdx >= 0 && nextIdx !== i ? positions[nextIdx] : null;
    
    let handles = { tanInX: 0, tanInY: 0, tanOutX: 0, tanOutY: 0 };
    
    if (splineType === SplineType.Bezier) {
      // Use per-point strength if available, otherwise fallback to global smoothness
      const pointSmoothness = perPointStrengths[i] ?? smoothness;
      handles = calculateAutoHandles(prev, curr, next, pointSmoothness);
    }
    
    result.push({
      x: curr.x,
      y: curr.y,
      ...handles,
    });
  }
  
  return result;
}

/**
 * Create a SplineEvaluator from Spline2D component data and a Level
 */
export function createSplineEvaluator(
  resolvePos: (guid: string) => { x: number; y: number } | null,
  splineData: Spline2D
): SplineEvaluator | null {
  const controlPoints = splineData.controlPoints ?? [];
  const splineType = splineData.splineType ?? SplineType.CatmullRom;
  const closed = splineData.closed ?? false;
  const tension = splineData.tension ?? 0.5;
  const smoothness = splineData.smoothness ?? 0.3;
  const perPointStrengths = splineData.controlPointStrengths ?? [];

  const resolvedPoints = resolveSplinePoints(resolvePos, controlPoints, splineType, smoothness, closed, perPointStrengths);
  
  if (resolvedPoints.length < 2) return null;
  
  return new SplineEvaluator(resolvedPoints, splineType, closed, tension);
}
